/**
 * JDA Median Login Module
 * 
 * Handles biometric authentication and auto-login functionality for the WordPress login page.
 * Integrates with the Median library for biometric authentication and JWT token management.
 * 
 * @package    Jda_Median
 * @subpackage Jda_Median/login
 * @author     JDA Worldwide
 */

'use strict';

// Create a global namespace for JDA Median login functionality
window.JDAMedianLogin = {};

let MEDIAN_LIBRARY_INITIALIZED = false;

// Constants
const SELECTORS = {
  LOGIN_MESSAGE: '#login-message',
  BIOMETRICS_OPTIN: '.js-biometrics-optin',
  BIOMETRICS_OPTIN_CONTAINER: '.js-show-for-biometrics',
  BIOMETRICS_PROMPT: '.biometrics-toggle__prompt',
  DATA_LOG: '#median-data-log',
  BIOMETRICS_LOGIN_BUTTON: '.js-biometrics-login',
  BIOMETRICS_BUTTON_CONTAINER: '.js-biometrics-login-container',
  TOUCHID_LOGIN_BUTTON: '.biometrics-button--touchid',
  FACEID_LOGIN_BUTTON: '.biometrics-button--faceid',
};

const COOKIES = {
  BIOMETRICS_OPTIN: 'biometricsOptIn'
};

const MESSAGES = {
  CONFIG: 'Missing WordPress configuration. Please ensure ajax_login_object is properly initialized.',
  LIBRARY_LOADED: 'Login: Median library initialized successfully',
  NONCE_REFRESH_ERROR: 'Failed to refresh security nonce:',
  LIBRARY_NOT_LOADED: 'Login: Median library not loaded. Please refresh the page.',
  AUTH_FAILED: 'Biometric authentication failed. Please try again or log in with your password.',
  TOKEN_REFRESHED: 'Authentication token refreshed successfully',
  TOKEN_EXPIRED: 'Your login token has expired. Please log in again with your password.',
  AJAX_ERROR: 'Network error during authentication:',
  TOUCH_ID_PROMPT: 'Enable Touch ID Login?',
  FACE_ID_PROMPT: 'Enable Face ID Login?',
  BIOMETRICS_PROMPT: 'Enable Biometrics Login?',
  LOGIN_PROMPT: 'Login with Touch ID',
  BIOMETRICS_NOT_AVAILABLE: 'Biometric authentication is not available on this device.',
  AUTH_STATUS_ERROR: 'Failed to check authentication status:',
  BIOMETRICS_DELETE_ERROR: 'Failed to delete biometric data:',
  MEDIAN_NOT_AVAILABLE: 'Median library is not available. Please refresh the page.',
  SAVE_SECRET_ERROR: 'Failed to save authentication secret:',
  LOGIN_REDIRECT_ERROR: 'Failed to redirect after login:',
  INVALID_AUTH_DATA: 'Invalid authentication data received',
  TOKEN_DELETED: 'Your login token has been deleted. Please log in again with your password.'
};

(($) => {

  /**
   * Extracts the most relevant error message from an AJAX error object.
   * Handles nested responseJSON, responseText, and fallback cases.
   * @param {Object} error - The error object from AJAX
   * @returns {string} - The extracted error message
   */
  function extractAjaxErrorMessage(error) {
    // 1. Check for responseJSON.message
    if (error.responseJSON && error.responseJSON.message) {
      return error.responseJSON.message;
    }
    // 2. Try to parse responseText as JSON and extract message
    if (error.responseText) {
      try {
        const parsed = JSON.parse(error.responseText);
        if (parsed.message) return parsed.message;
        if (parsed.data && parsed.data.message) return parsed.data.message;
      } catch (e) {
        // Not JSON, ignore
      }
    }
    // 3. Fallback to error.message or stringified error
    if (error.message) return error.message;
    return JSON.stringify(error);
  }

  /**
   * Deletes biometric authentication data from Median and hides the biometrics button container
   */
  function deleteAuth() {
    median.auth.delete({
      callbackFunction: (response) => {
        console.log('Biometric data deleted:', response);
        $(SELECTORS.BIOMETRICS_OPTIN).prop('checked', false);
        $(SELECTORS.BIOMETRICS_BUTTON_CONTAINER).hide();
      }
    });
  }

  /**
   * Updates the status display with authentication data
   * @param {Object} data - Authentication status data
   * @param {string} [data.biometryType] - Type of biometric authentication available
   * @param {boolean} [data.hasTouchId] - Whether device supports biometrics
   * @param {boolean} [data.hasAndroidFingerprint] - Whether device supports Android biometrics
   * @param {boolean} [data.hasSecret] - Whether device has stored a secret
   * @returns {void}
   */
  const handleAuthStatus = (data) => {
    // Validate input data
    if (!data) {
      console.error(MESSAGES.INVALID_AUTH_DATA);
      return;
    }

    // debugging
    // $(SELECTORS.DATA_LOG).text(JSON.stringify(data, null, 2));

    if (!data.hasTouchId) {
      console.warn(MESSAGES.BIOMETRICS_NOT_AVAILABLE);
      return;
    }

    // Show biometrics container
    $(SELECTORS.BIOMETRICS_OPTIN_CONTAINER).show();

    // Set appropriate prompt text based on biometry type
    let LOGIN_PROMPT_TEXT = MESSAGES.TOUCH_ID_PROMPT;

    if (data.biometryType === 'touchId' || data.hasAndroidFingerprint) {
      $(SELECTORS.TOUCHID_LOGIN_BUTTON).css('display', 'flex');
    } else {
      LOGIN_PROMPT_TEXT = MESSAGES.FACE_ID_PROMPT;
      $(SELECTORS.FACEID_LOGIN_BUTTON).css('display', 'flex');
    }

    $(SELECTORS.BIOMETRICS_PROMPT).text(LOGIN_PROMPT_TEXT);

    // Show login button if secret exists
    if (data.hasSecret) {
      $(SELECTORS.BIOMETRICS_OPTIN).prop('checked', true);
      $(SELECTORS.BIOMETRICS_BUTTON_CONTAINER).show();
    }

    // Check URL parameters to determine if we should auto-login
    const urlParams = new URLSearchParams(window.location.search);

    // Disable autologin in specific cases
    if (urlParams.get('loggedout')) {
      console.log('Auto-login disabled: User just logged out');
      return;
    }

    if (urlParams.get('action')) {
      console.log('Auto-login disabled: Action parameter present:', urlParams.get('action'));
      return;
    }

    // Start biometrics authentication and autologin flow
    if (data?.hasTouchId && data?.hasSecret) {
      // Verify median library is available
      if (typeof window.median?.auth?.get !== 'function') {
        console.error(MESSAGES.MEDIAN_NOT_AVAILABLE);
        return;
      }

      median.auth.get({
        callbackFunction: JDAMedianLogin.handleGetAuth,
        prompt: MESSAGES.LOGIN_PROMPT
      }).catch(error => {
        console.error('Failed to get authentication:', error);
        $(SELECTORS.DATA_LOG).text(MESSAGES.AUTH_FAILED);
      });
    }
  };

  /**
   * Refreshes the security nonce
   * @async
   * @throws {Error} If WordPress configuration is missing or AJAX call fails
   * @returns {Promise<string>} The new nonce
   */
  async function refreshNonce() {
    if (!ajax_login_object?.ajaxurl || !ajax_login_object?.refresh_nonce_action) {
      throw new Error(MESSAGES.CONFIG);
    }

    try {
      const response = await $.ajax({
        url: ajax_login_object.ajaxurl,
        type: 'POST',
        data: {
          action: ajax_login_object.refresh_nonce_action
        }
      });

      if (response?.success && response?.data?.security) {
        ajax_login_object.security = response.data.security;
        return response.data.security;
      }
      throw new Error('Invalid nonce refresh response');
    } catch (error) {
      console.error(MESSAGES.NONCE_REFRESH_ERROR, error);
      throw error;
    }
  }

  /**
   * Attempts to log in the user with the provided token
   * @async
   * @param {string} token - JWT token for authentication
   * @throws {Error} If WordPress configuration is missing or AJAX call fails
   * @returns {Promise<void>}
   */
  async function logInUser(token) {
    // Validate token
    if (!token) {
      console.error('Invalid token provided to logInUser');
      $(SELECTORS.DATA_LOG).text(MESSAGES.AUTH_FAILED);
      return;
    }

    // Only proceed if we're on the login page
    if (!$('body').hasClass('login-action-login')) {
      console.warn('Not on login page, skipping auto-login');
      return;
    }

    try {
      // Validate ajax configuration
      if (!ajax_login_object?.ajaxurl) {
        throw new Error(MESSAGES.CONFIG);
      }

      // Refresh nonce before attempting login
      try {
        await refreshNonce();
      } catch (error) {
        console.error('Failed to refresh nonce before login:', error);
        throw error;
      }

      const { ajaxurl: ajax_url, security: nonce } = ajax_login_object;

      if (!ajax_url || !nonce) {
        throw new Error(MESSAGES.CONFIG);
      }

      // Perform login AJAX request
      const response = await $.ajax({
        type: 'POST',
        dataType: 'json',
        url: ajax_url,
        data: {
          action: 'auto_login',
          token,
          security: nonce
        }
      });

      const { success, data } = response || {};

      // Handle unsuccessful login
      if (!success || !data?.token) {
        console.error('Login failed:', response);
        $(SELECTORS.DATA_LOG).text(data?.message || MESSAGES.AUTH_FAILED);

        // Delete the invalid secret if median is available
        if (typeof window.median?.auth?.delete === 'function') {
          deleteAuth();
        }
        return;
      }

      // Create secret object and redirect URL
      const secret = { token: data.token };
      const redirectUrl = `/wp-json/simple-jwt-login/v1/autologin?JWT=${data.token}`;

      // Save the new token in median
      if (typeof window.median?.auth?.save !== 'function') {
        console.error(MESSAGES.MEDIAN_NOT_AVAILABLE);
      } else {
        try {
          await median.auth.save({ secret });
          console.log('Secret saved successfully');
        } catch (saveError) {
          console.error(MESSAGES.SAVE_SECRET_ERROR, saveError);
          // Continue with redirect even if save fails
        }
      }

      // Redirect based on device type
      try {
        if (navigator.userAgent.match(/Android/i)) {
          document.location = redirectUrl;
        } else {
          window.location.replace(redirectUrl);
        }
      } catch (redirectError) {
        console.error(MESSAGES.LOGIN_REDIRECT_ERROR, redirectError);
        $(SELECTORS.DATA_LOG).text('Redirect failed. Please try again.');
      }

    } catch (error) {
      const errorMessage = `${extractAjaxErrorMessage(error)} Please log in again.`;
      $(SELECTORS.DATA_LOG).text(errorMessage);
      console.error(errorMessage);
      deleteAuth();
    }
  };

  /**
   * Handles the authentication response from Median
   * @param {Object} data - Authentication response data
   * @param {boolean} data.success - Whether authentication was successful
   * @param {string} [data.secret] - The stored secret if authentication successful
   * @param {string} [data.error] - Error message if authentication failed
   * @returns {void}
   */
  const handleGetAuth = (data) => {
    // Validate response data
    if (!data) {
      console.error(MESSAGES.INVALID_AUTH_DATA);
      $(SELECTORS.DATA_LOG).text(MESSAGES.AUTH_FAILED);
      return;
    }

    // Process successful authentication
    if (data.success && data.secret) {
      try {
        // Parse the secret JSON
        const secret = JSON.parse(data.secret);

        if (!secret || !secret.token) {
          throw new Error('Invalid token format in secret');
        }

        // Attempt login with the retrieved token
        logInUser(secret.token).catch(error => {
          console.error('Login failed:', error);
          $(SELECTORS.DATA_LOG).text(MESSAGES.AUTH_FAILED);
        });
      } catch (error) {
        console.error('Failed to parse authentication data:', error);
        $(SELECTORS.DATA_LOG).text(MESSAGES.AUTH_FAILED);
      }
    } else {
      // Handle authentication failure
      const errorMessage = data.error || MESSAGES.AUTH_FAILED;
      console.error('Authentication failed:', errorMessage);
      $(SELECTORS.DATA_LOG).text(errorMessage);
    }
  };

  // Initialize biometrics opt-in handler - use event delegation for dynamically added elements
  $(document).on('change', SELECTORS.BIOMETRICS_OPTIN, function () {
    // Set cookie for biometrics opt-in status
    if (typeof JDAMedian?.setCookie !== 'function') {
      console.error('JDAMedian global object not available');
    } else {
      JDAMedian.setCookie(COOKIES.BIOMETRICS_OPTIN, this.checked);
    }

    // Handle opt-out by deleting stored secret
    if (!this.checked) {
      if (typeof window.median?.auth?.delete !== 'function') {
        console.error(MESSAGES.MEDIAN_NOT_AVAILABLE);
        return;
      }

      $(SELECTORS.DATA_LOG).text(MESSAGES.TOKEN_DELETED);
      deleteAuth();
    }

    // Log current cookie value
    if (typeof JDAMedian?.getCookie === 'function') {
      console.log(`${COOKIES.BIOMETRICS_OPTIN}: ${JDAMedian.getCookie(COOKIES.BIOMETRICS_OPTIN)}`);
    }
  });

  $(document).on('click', SELECTORS.BIOMETRICS_LOGIN_BUTTON, function () {
    // Verify median library is available
    if (typeof window.median?.auth?.status !== 'function') {
      console.error(MESSAGES.MEDIAN_NOT_AVAILABLE);
      $(SELECTORS.DATA_LOG).text(MESSAGES.LIBRARY_NOT_LOADED);
      return;
    }

    median.auth.status()
      .then((result) => {
        console.log('Auth status result:', result);

        // Validate the result
        if (!result) {
          throw new Error('Invalid auth status response');
        }

        // Check if biometric auth is available and secret exists
        if (result.hasTouchId && result.hasSecret) {
          // Verify get method is available
          if (typeof window.median?.auth?.get !== 'function') {
            throw new Error(MESSAGES.MEDIAN_NOT_AVAILABLE);
          }

          return median.auth.get({
            callbackFunction: JDAMedianLogin.handleGetAuth,
            prompt: MESSAGES.LOGIN_PROMPT
          });
        } else {
          // Biometrics or secret not available
          const reason = !result.hasTouchId ? 'Biometrics not available' : 'No saved credentials';
          console.warn(`Biometric login not possible: ${reason}`);
          $(SELECTORS.DATA_LOG).text(MESSAGES.TOKEN_EXPIRED);
        }
      })
      .catch(error => {
        console.error(MESSAGES.AUTH_STATUS_ERROR, error);
        $(SELECTORS.DATA_LOG).text(MESSAGES.AUTH_FAILED);
      });
  });

  // Expose functions to the global namespace
  Object.assign(window.JDAMedianLogin, {
    handleAuthStatus,
    handleGetAuth,
    refreshNonce
  });

})(jQuery);

function jda_median_init() {


  const STAGING_DOMAIN_REGEX = 'https?:\/\/chmportal\.flywheelsites\.com';
  const PRODUCTION_DOMAIN_REGEX = 'https?:\/\/member-portal\.chministries\.org';
  const ACTIVE_DOMAIN = PRODUCTION_DOMAIN_REGEX;

  var rulesArray = [
    {
      "id": 1,
      "mode": "external",
      "label": "Non-web links",
      "pagesToTrigger": "custom",
      "regex": "^(?!https?://).*"
    },
    {
      "id": 2,
      "mode": "appbrowser",
      "label": "Facebook",
      "pagesToTrigger": "custom",
      "regex": "https?://([-\\w]+\\.)*facebook\\.com.*"
    },
    {
      "id": 3,
      "mode": "appbrowser",
      "label": "Twitter/X",
      "pagesToTrigger": "custom",
      "regex": "https?://([\\-\\w]+\\.)*(twitter|x)\\.com/.*"
    },
    {
      "id": 4,
      "mode": "appbrowser",
      "label": "Instagram",
      "pagesToTrigger": "custom",
      "regex": "https?://([-\\w]+\\.)*instagram\\.com/.*"
    },
    {
      "id": 5,
      "mode": "appbrowser",
      "label": "Google Maps",
      "pagesToTrigger": "custom",
      "regex": "https?://maps\\.google\\.com.*"
    },
    {
      "id": 6,
      "mode": "appbrowser",
      "label": "Google Maps Search",
      "pagesToTrigger": "custom",
      "regex": "https?://([-\\w]+\\.)*google\\.com/maps/search/.*"
    },
    {
      "id": 7,
      "mode": "appbrowser",
      "label": "LinkedIn",
      "pagesToTrigger": "custom",
      "regex": "https?://([-\\w]+\\.)*linkedin\\.com/.*"
    },
    {
      "id": 8,
      "mode": "internal",
      "label": "All pages on my staging domain",
      "pagesToTrigger": "domain",
      "regex": "https?:\/\/([-\w]+\.)*flywheelsites\.com(\\/.*)?$"
    },
    {
      "id": 9,
      "mode": "internal",
      "label": "All pages on my production domain",
      "pagesToTrigger": "domain",
      "regex": "https?:\/\/member-portal\.chministries\.org(\\/.*)?$"
    },
    {
      "id": 10,
      "mode": "internal",
      "label": "All Other Links",
      "pagesToTrigger": "all",
      "regex": ".*"
    }
  ];

  median.internalExternal.set({
    "rules": rulesArray
  });
  console.log('Median link handling rules set successfully');

  var menuItems = {
    active: true,
    titles: [
      {
        "regex": `${ACTIVE_DOMAIN}\/wp-login\.php(?:\\?[^#\\s]*)?`,
        "showImage": true,
      },
      {
        "regex": `${ACTIVE_DOMAIN}\/register\\/?$`,
        "showImage": true,
      },
      {
        "regex": `${ACTIVE_DOMAIN}\/?$`,
        "showImage": true,
      },
      {
        "regex": ".*",
        "showImage": true,
      }
    ]
  }
  median.navigationTitles.set({
    'persist': true,
    'data': menuItems
  });
  console.log('Median navigation titles set successfully');

  var autoNewWindowsRules = {
    active: true,
    persist: true,
    levels: [
      {
        "level": 1,
        "label": "Home",
        "regex": ACTIVE_DOMAIN,
      },
      {
        "level": 1,
        "label": "Login Page",
        "regex": `${ACTIVE_DOMAIN}\/wp-login\.php(?:\\?[^#\\s]*)?`,
      },
      {
        "level": 1,
        "label": "Register Page",
        "regex": `${ACTIVE_DOMAIN}\/register\/`,
      },
      {
        "level": 1,
        "label": "WP JSON",
        "regex": `${ACTIVE_DOMAIN}\/wp-json(\\/)?([^#\\s]*)?`,
      },
      {
        "level": 1,
        "label": "Account",
        "regex": `${ACTIVE_DOMAIN}\/account\\/?$`,
      },
      {
        "level": 1,
        "label": "Blog",
        "regex": `${ACTIVE_DOMAIN}\/blog\\/?$`,
      },
      {
        "level": 1,
        "label": "Resources",
        "regex": `${ACTIVE_DOMAIN}\/resources\\/?$`,
      },
      {
        "level": 2,
        "label": "Account",
        "regex": `${ACTIVE_DOMAIN}\/account\/(?:([^/]*))`,
      },
      {
        "level": 2,
        "label": "Blog Posts",
        "regex": `${ACTIVE_DOMAIN}\/blog\/(?:([^/]*))`,
        "custom": true
      },
      {
        "level": 2,
        "label": "Resources",
        "regex": `${ACTIVE_DOMAIN}\/resource\/(?:([^/]*))`,
        "custom": true
      },
      {
        "level": 3,
        "label": "New Need",
        "regex": `${ACTIVE_DOMAIN}\/new-need\/?$`,
      },
      {
        "level": 3,
        "label": "Update Incident",
        "regex": `${ACTIVE_DOMAIN}\/update-incident\/`,
      }
    ]
  }
  median.navigationLevels.set(autoNewWindowsRules);
  console.log('Median navigation levels set successfully');
}

/**
 * Attempts authentication when the Median library is ready.
 * This function must be globally accessible to be called by the Median framework.
 */
function median_library_ready() {
  console.log(MESSAGES.LIBRARY_LOADED);

  // Initialize Median configuration
  // jda_median_init();

  median.firebaseAnalytics.event.logEvent({
    'event': 'median_library_ready',
    'data': {}
  });

  // Display biometrics login form elements in CSS grid
  if (navigator.userAgent.indexOf('median') > -1) {
    document.querySelector('body.login #login form').style.display = "grid";
  }

  if (document.querySelector(SELECTORS.LOGIN_MESSAGE)) {
    SELECTORS.DATA_LOG = SELECTORS.LOGIN_MESSAGE;
  }

  try {

    // Verify functions exist before using them
    if (typeof window.median?.auth?.status !== 'function') {
      throw new Error(MESSAGES.MEDIAN_NOT_AVAILABLE);
    }

    if (typeof window.JDAMedianLogin?.handleAuthStatus !== 'function') {
      throw new Error('JDAMedianLogin.handleAuthStatus function not available');
    }

    // Check authentication status
    median.auth.status({ callbackFunction: JDAMedianLogin.handleAuthStatus });

  } catch (error) {
    console.error('Error in median_library_ready:', error.message);
  }
}
